import React from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { withRouter } from 'react-router';
import { objectOf, string, node, func, bool, any } from 'prop-types';
import {
  submit,
  destroy,
  isValid,
  isDirty,
  getFormSubmitErrors,
  SubmissionError
} from 'redux-form';
import {
  find,
  omitBy,
  isEmpty,
  isUndefined,
  last,
  findIndex,
  partial
} from 'lodash';
import cx from 'classnames';

import Step from '@material-ui/core/Step';
import Stepper from '@material-ui/core/Stepper';
import StepButton from '@material-ui/core/StepButton';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import WarningIcon from '@material-ui/icons/Warning';

import styles from './styles/index.scss';
import { flattenObject } from 'helpers/misc';
import { PropTypes, injectInto, maybeProp } from 'helpers/react';
import { requireFields } from 'helpers/redux/form';
import { showNotification } from 'modules/snackbar';

const { routerShape, wizardConfigShape } = PropTypes;

export class Wizard extends React.Component {
  static propTypes = {
    formName: string.isRequired,
    config: wizardConfigShape.isRequired,
    // fixme: hack to get around RR v4 not supporting relative URLs
    // https://github.com/ReactTraining/react-router/issues/2172
    baseRoutePath: string.isRequired,
    currentPageNode: node.isRequired,
    children: func.isRequired,
    onClose: func.isRequired,
    isLoading: bool,
    // try to create your resource, return a resolved promise if OK or a
    // rejected promise with a SubmissionError containing an object of errors like
    // {
    //   [fieldName]: 'error message'
    //   _error: 'form-wide error message'
    // }
    onSubmit: func.isRequired,
    initialValues: objectOf(any),

    // injected props:
    router: routerShape.isRequired,
    isValid: bool.isRequired,
    isDirty: bool.isRequired,
    submitErrors: objectOf(string),
    submit: func.isRequired,
    destroy: func.isRequired,
    push: func.isRequired,
    showNotification: func.isRequired,
    showBackButton: bool,
    hideStepper: bool,
    submitButtonText: string,
    showPreSubmitConfirmation: bool,
    preSubmitConfirmationData: objectOf(any),
    PreSubmitConfirmationComponent: func,
    location: objectOf(any)
  };
  static defaultProps = {
    submitErrors: {},
    submitButtonText: ''
  };

  state = {
    confirmingLeave: false,
    confirmingPreSubmit: false,
    openDialog: false
  };

  validateWizard = values => {
    // omitBy({
    //   // run validations for each step
    //   ...this.props.config.steps.reduce((result, { validate }) => {
    //     return isFunction(validate)
    //       ? { ...result, ...validate(values) }
    //       : result;
    //   }, {}),
    //
    //   // validate required fields for each step
    //   ...requireFields(
    //     flatMap(this.props.steps, 'requiredFields')
    //   )(values)
    // }, isUndefined);

    return omitBy(
      {
        ...this.props.config.model.validate(values),
        ...requireFields(this.props.config.model.requiredFields)(values)
      },
      isUndefined
    );
  };

  handleOpenDialog = () => {
    this.setState(
      {
        openDialog: true
      },
      this.renderNoDefaultDagDialog
    );
  };

  handleCloseDialog = () => {
    this.setState({
      openDialog: false
    });
  };

  handleSubmit = values => {
    this.handleCloseDialog();
    const currentStepIndex = this.getCurrentStepIndex();

    if (currentStepIndex === this.props.config.steps.length - 1) {
      return Promise.resolve(this.validateWizard(values))
        .then(localValidationErrors => {
          const nestedValidationErrors = Object.values(
            flattenObject(localValidationErrors)
          ).filter(e => e);

          if (
            !isEmpty(localValidationErrors) &&
            !isEmpty(nestedValidationErrors)
          ) {
            this.props.showNotification(
              'Validation failed; please go back and fix the marked fields before trying again.'
            );

            return Promise.reject(new SubmissionError(localValidationErrors));
          }

          return values;
        })
        .then(values => {
          return this.props.onSubmit(values);
        })
        .then(this.handleSubmissionSuccess)
        .catch(this.handleSubmissionFailure);
    }

    return this.transitionToStep(
      this.props.config.steps[this.getCurrentStepIndex() + 1]
    );
  };

  handleSubmissionSuccess = vals => {
    this.props.destroy(this.props.formName);
  };

  handleSubmissionFailure = error => {
    if (error.name !== 'SubmissionError' && __DEV__) {
      console.warn(
        'Submission failure errors should be an instance of redux-form/SubmissionError.',
        error
      );
    }

    // let redux-form catch/display the errors
    throw error;
  };

  submitForm = () => {
    const { location, preSubmitConfirmationData } = this.props;
    if (
      location.pathname === '/engines/overview/new/testing-detail' &&
      (isUndefined(preSubmitConfirmationData.singleEngineTdoJobJson) ||
        isUndefined(preSubmitConfirmationData.singleEngineUploadJobJson))
    ) {
      this.handleOpenDialog();
    } else {
      this.props.submit(this.props.formName);
    }
  };

  transitionToStep = ({ path }) => {
    this.props.push(`${this.props.baseRoutePath}${path}`);
  };

  handleWizardExit = () => {
    this.props.destroy(this.props.formName);
    this.props.onClose(); // todo: what is this?
  };

  confirmLeave = () => {
    if (!this.props.isDirty) {
      return this.handleWizardExit();
    }

    this.setState({
      confirmingLeave: true
    });
  };

  cancelLeaving = () => {
    this.setState({
      confirmingLeave: false
    });
  };

  getCurrentStepPath = () => {
    return last(this.props.router.routes).path;
  };

  getCurrentStepIndex = () => {
    return findIndex(this.props.config.steps, {
      path: this.getCurrentStepPath()
    });
  };

  getCurrentStep = () => {
    return this.props.config.steps[this.getCurrentStepIndex()];
  };

  getStepsWithErrors(errors) {
    return Object.keys(
      omitBy(flattenObject(errors), isUndefined)
    ).map(errorField => {
      const page =
        find(this.props.config.steps, step => {
          return step.fields.includes(errorField);
        }) || {};

      if (__DEV__ && !page.path) {
        console.warn(
          "WARNING: validation found, but doesn't correspond to any page!",
          errorField
        );
      }

      return page.path;
    });
  }

  renderStepper = () => {
    const currentStepIndex = this.getCurrentStepIndex();
    const stepsWithSubmitErrors = this.getStepsWithErrors(
      this.props.submitErrors
    );

    return (
      <Stepper style={{ width: '70%', height: '72px' }}>
        {this.props.config.steps.map((step, i) =>
          <Step
            active={i === currentStepIndex}
            completed={i < currentStepIndex}
            disabled={i > currentStepIndex}
            key={step.path}
            className={styles['step']}
          >
            <StepButton
              onClick={partial(this.transitionToStep, step)}
              {...maybeProp(
                // hack: if icon prop exists, even if undefined,
                // no non-custom icons will show
                'icon',
                <WarningIcon className={styles.warningIcon} />,
                stepsWithSubmitErrors.includes(step.path)
              )}
            >
              <div className={styles['step-content']}>
                <div>
                  {step.label}
                </div>
                {step.optional &&
                  <span className={styles['step-optional']}>Optional</span>}
              </div>
            </StepButton>
          </Step>
        )}
      </Stepper>
    );
  };

  renderButtons = ({ className } = {}) => {
    const {
      showPreSubmitConfirmation,
      isLoading,
      config,
      isValid,
      showBackButton
    } = this.props;
    const currentStepIndex = this.getCurrentStepIndex();
    const currentStep = this.getCurrentStep();
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === config.steps.length - 1;
    const isBackButton = showBackButton && !isFirstStep;
    const isDisabledButton = !isValid || isLoading;
    if (!currentStep) {
      return null;
    }

    return (
      !currentStep.hideButtons &&
      <span>
        <Button
          className={cx(styles.btn)}
          onClick={
            isBackButton
              ? partial(
                  this.transitionToStep,
                  config.steps[currentStepIndex - 1]
                )
              : this.confirmLeave
          }
        >
          {isBackButton ? 'Back' : 'Cancel'}
        </Button>
        {false &&
          <Button className={cx(className, styles.btn)} disabled>
            Save & Finish Later
          </Button>}
        {!isLoading && showPreSubmitConfirmation && isLastStep
          ? <Button
              variant="contained"
              type="submit"
              color="primary"
              className={cx(className, styles.btn)}
              onClick={this.showPreSubmitConfirmation}
              disabled={isDisabledButton}
            >
              {this.props.isLoading &&
                <CircularProgress
                  className={cx(styles.circularProgressMargin, {
                    [styles.circularProgressColor]: !isDisabledButton
                  })}
                  size={35}
                />}
              Review
            </Button>
          : <Button
              type="submit"
              variant="contained"
              color="primary"
              className={cx(className, styles.btn)}
              onClick={this.submitForm}
              disabled={isDisabledButton}
            >
              {this.props.isLoading
                ? <CircularProgress
                    className={cx(styles.circularProgressMargin, {
                      [styles.circularProgressColor]: !isDisabledButton
                    })}
                    size={35}
                  />
                : null}
              {isLoading
                ? null
                : currentStepIndex === config.steps.length - 1
                  ? 'Submit'
                  : 'Next'}
            </Button>}
      </span>
    );
  };

  renderConfirmationDialog = () =>
    <Dialog open={this.state.confirmingLeave} fullWidth>
      <DialogTitle>Are you sure you want to exit?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You will lose any unsaved changes.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={this.cancelLeaving}
          className={styles.btn}
        >
          Stay
        </Button>
        <Button
          color="primary"
          onClick={this.handleWizardExit}
          className={styles.btn}
        >
          Leave anyway
        </Button>
      </DialogActions>
    </Dialog>;

  renderPreSubmitConfirmation = () => {
    const {
      preSubmitConfirmationData,
      PreSubmitConfirmationComponent
    } = this.props;
    return (
      PreSubmitConfirmationComponent &&
      <PreSubmitConfirmationComponent
        preSubmitConfirmationData={preSubmitConfirmationData}
        open={this.state.confirmingPreSubmit}
        onClose={this.cancelPreSubmitConfirmation}
        onSubmit={this.handleSubmit}
      />
    );
  };

  showPreSubmitConfirmation = event => {
    this.setState({
      confirmingPreSubmit: true
    });
  };

  cancelPreSubmitConfirmation = event => {
    this.setState({
      confirmingPreSubmit: false
    });
  };

  renderNoDefaultDagDialog = () => {
    const { openDialog } = this.state;
    return (
      <Dialog open={openDialog} onClose={this.handleCloseDialog} fullWidth>
        <DialogTitle>Are you sure you want to continue?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By failing to provide a default job mutation your engine will be
            unable to be run in CMS, set as public, and enrolled in engine
            certification. You can always come back and add these later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={this.handleCloseDialog}
            color="primary"
            className={styles.btn}
          >
            Go back
          </Button>
          <Button
            onClick={this.handleSubmit}
            color="primary"
            className={styles.btn}
          >
            Continue anyway
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const currentPage = injectInto(this.props.currentPageNode, {
      onSubmit: this.handleSubmit,
      validate: this.validateWizard,
      initialValues:
        this.props.initialValues || this.props.config.model.initialValues,
      fields: this.props.config.model.fields
    });
    return this.props.children({
      currentPage: currentPage,
      renderStepper: this.renderStepper,
      renderButtons: this.renderButtons,
      requestClose: this.confirmLeave,
      renderConfirmationDialog: this.renderConfirmationDialog,
      renderPreSubmitConfirmation: this.renderPreSubmitConfirmation,
      renderNoDefaultDagDialog: this.renderNoDefaultDagDialog,
      hideStepper: this.props.hideStepper
    });
  }
}

export default connect(
  (state, ownProps) => ({
    isValid: isValid(ownProps.formName)(state),
    isDirty: isDirty(ownProps.formName)(state),
    submitErrors: getFormSubmitErrors(ownProps.formName)(state)
  }),
  {
    submit,
    destroy,
    push,
    showNotification
  }
)(withRouter(Wizard));



// WEBPACK FOOTER //
// ./src/client/shared-components/Wizard/index.js